import React, { useState } from 'react';
import { MessageCircle, X, Send, Phone } from 'lucide-react';
import { Button } from './ui/button';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: "Bonjour ! Je suis l'assistant virtuel de Cabinet CAMBC Conseils. Comment puis-je vous aider aujourd'hui ?",
      time: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  const quickReplies = [
    "Assurance temporaire",
    "Demander un devis",
    "Carte grise",
    "Parler à un conseiller"
  ];

  const botResponses = {
    "assurance temporaire": "Notre service d'assurance temporaire est disponible 24h/24 et 7j/7 ! Vous pouvez souscrire de 1 à 90 jours. Souhaitez-vous faire une demande maintenant ? Rendez-vous sur notre page dédiée ou appelez-nous au 05 82 81 91 20.",
    "devis": "Je serais ravi de vous aider à obtenir un devis gratuit ! Vous pouvez remplir notre formulaire en ligne ou nous appeler directement au 05 82 81 91 20 pour un conseil personnalisé.",
    "carte grise": "Nous proposons un service complet pour vos démarches carte grise : immatriculation, changement de propriétaire, duplicata... Consultez notre page Carte Grise ou contactez-nous pour plus d'informations.",
    "conseiller": "Bien sûr ! Vous pouvez nous joindre au 05 82 81 91 20 (du lundi au vendredi, 9h-18h) ou par email à cabinet@cambc-conseils.fr. Pour les urgences assurance temporaire, nous sommes disponibles 24h/24 !",
    "default": "Merci pour votre message ! Pour une réponse personnalisée, n'hésitez pas à nous appeler au 05 82 81 91 20 ou à utiliser notre formulaire de contact. Nous vous répondrons dans les plus brefs délais."
  };

  const handleSend = (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      text: text,
      time: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Simulate bot response
    setTimeout(() => {
      const lowerText = text.toLowerCase();
      let response = botResponses.default;

      if (lowerText.includes('temporaire')) {
        response = botResponses["assurance temporaire"];
      } else if (lowerText.includes('devis')) {
        response = botResponses["devis"];
      } else if (lowerText.includes('carte grise') || lowerText.includes('immatriculation')) {
        response = botResponses["carte grise"];
      } else if (lowerText.includes('conseiller') || lowerText.includes('parler') || lowerText.includes('appeler')) {
        response = botResponses["conseiller"];
      }

      const botMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: response,
        time: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  const handleQuickReply = (reply) => {
    handleSend(reply);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'bg-slate-700' : 'bg-blue-600 hover:bg-blue-700'
        }`}
        data-testid="chat-toggle-button"
        aria-label="Ouvrir le chat"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <MessageCircle size={24} className="text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-44 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Cabinet CAMBC Conseils</h3>
                <p className="text-xs text-blue-100">En ligne • Répond instantanément</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                    message.type === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white text-slate-700 shadow-sm rounded-bl-md'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
          </div>

          {/* Quick Replies */}
          <div className="px-4 py-2 bg-white border-t border-slate-100">
            <div className="flex flex-wrap gap-2">
              {quickReplies.map((reply) => (
                <button
                  key={reply}
                  onClick={() => handleQuickReply(reply)}
                  className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 rounded-full transition-colors"
                >
                  {reply}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Écrivez votre message..."
                className="flex-1 px-4 py-2 bg-slate-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="chat-input"
              />
              <button
                onClick={() => handleSend()}
                className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-full flex items-center justify-center text-white transition-colors"
                data-testid="chat-send-button"
              >
                <Send size={18} />
              </button>
            </div>
            <div className="mt-3 text-center">
              <a 
                href="tel:0582819120" 
                className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-blue-600 transition-colors"
              >
                <Phone size={12} />
                Préférez-vous appeler ? 05 82 81 91 20
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
